import React, { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { 
  formatHorizontalBarLabel, 
  getHorizontalBarChartConfig,
  formatCurrency 
} from './DrillDown/formatters';
import { HorizontalBarChartSkeleton } from './Skeletons/ChartSkeletons';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Box,
  useTheme,
  Paper,
  Chip
} from '@mui/material';

interface TopChartWithTableProps {
  data: any[];
  loading?: boolean;
  title: string;
  color?: string | string[];
  valueKey?: string; // Chave para o valor (padrão: 'total')
  nameKey?: string; // Chave para o nome (padrão: 'nome')
  onItemClick?: (item: any) => void;
  height?: number;
  tableTitle?: string; // Título da tabela (padrão: 'Demais Registros')
  showTotal?: boolean; // Se deve mostrar total nos valores
  formatValue?: (value: number) => string; // Função customizada para formatar valores
  hasLongLabels?: boolean;
  useDynamicColors?: boolean; // Se deve usar cores dinâmicas baseadas no saldo (padrão: false)
}

const TopChartWithTable: React.FC<TopChartWithTableProps> = ({
  data,
  loading = false,
  title,
  color = '#1976d2',
  valueKey = 'total',
  nameKey = 'nome',
  onItemClick,
  height = 590,
  tableTitle = 'Demais Registros',
  showTotal = true,
  formatValue = formatCurrency,
  hasLongLabels = true,
  useDynamicColors = false
  }) => {
    const theme = useTheme();
    
    const safeData = useMemo(() => {
      if (!Array.isArray(data)) {
        if (data !== undefined && data !== null) {
          console.warn(`[TopChartWithTable] Dados inválidos recebidos para "${title}". Esperado array, recebido:`, data);
        }
        return [];
      }
      return data;
    }, [data, title]);

  // Separar dados em TOP 10 para gráfico e restante para tabela
  const { chartData, tableData } = useMemo(() => {
    if (!safeData || safeData.length === 0) {
      return { chartData: [], tableData: [] };
    }

    // Os dados já vêm ordenados do backend (saldo decrescente: débitos - créditos)
    // Top 10 = maior saldo positivo (precisa pagar mais ICMS)
    console.log('📊 [DEBUG PREPARE] Top 10 dados do backend:', safeData.slice(0, 10).map(d => ({
      nome: d[nameKey],
      valorOriginal: d[valueKey],
      cod_item: (d as any).cod_item
    })));

    // Top 10 para o gráfico
    const top10Data = safeData.slice(0, 10).map((item, index) => {
      const valorOriginal = item[valueKey] as number;
      const isPositivo = valorOriginal >= 0;
      
      // Determinar cor da barra
      let corBarra: string;
      if (useDynamicColors) {
        // CONCEITO CONTÁBIL CORRETO:
        // Saldo POSITIVO (débitos > créditos) = deve pagar = VERMELHO
        // Saldo NEGATIVO (créditos > débitos) = tem crédito = VERDE
        corBarra = isPositivo ? '#f44336' : '#4caf50';
      } else {
        // Usar cor fixa passada como prop
        corBarra = typeof color === 'string' ? color : color[0] || '#1976d2';
      }
      
      return {
        ...item,
        nomeCompleto: item[nameKey] as string, // Nome completo original
        [nameKey]: formatHorizontalBarLabel(item[nameKey] as string, 30), // Nome truncado
        valorOriginal: valorOriginal, // Valor com sinal (para cor e tooltip)
        valorDisplay: Math.abs(valorOriginal), // Valor absoluto (para tamanho da barra)
        indiceOriginal: index, // Índice na ordem do backend
        corBarra: corBarra
      };
    });
    
    // Para gráfico horizontal, ordenar CRESCENTE (menor em cima, maior embaixo)
    const top10 = top10Data.sort((a, b) => (a.valorDisplay as number) - (b.valorDisplay as number));

    console.log('📊 [DEBUG PREPARE] Top 10 preparado para gráfico:', top10.map(d => ({
      indice: d.indiceOriginal,
      nomeCompleto: d.nomeCompleto,
      nomeTruncado: d[nameKey],
      valorOriginal: d.valorOriginal,
      valorDisplay: d.valorDisplay,
      cor: d.corBarra,
      cod_item: (d as any).cod_item
    })));

    // Restante para tabela
    const restante = safeData.slice(10);

    return { 
      chartData: top10,
      tableData: restante
    };
  }, [safeData, valueKey, nameKey, color, useDynamicColors]);

  // Encontra o valor máximo ABSOLUTO nos dados do gráfico para usar como referência de legibilidade
  const maxValue = useMemo(() => {
    if (chartData.length === 0) return 1;
    return Math.max(...chartData.map(d => (d as any).valorDisplay || Math.abs(d[valueKey] as number)));
  }, [chartData, valueKey]);

  const resolveOriginalItem = (chartItem: any) => {
    if (!chartItem || safeData.length === 0) {
      return null;
    }

    if (typeof chartItem.indiceOriginal === 'number' && safeData[chartItem.indiceOriginal]) {
      return safeData[chartItem.indiceOriginal];
    }

    if (chartItem.nomeCompleto) {
      const itemByName = safeData.find(item => item[nameKey] === chartItem.nomeCompleto);
      if (itemByName) {
        return itemByName;
      }
    }

    if ((chartItem as any).cod_item) {
      const itemByCode = safeData.find(item =>
        (item as any).cod_item === (chartItem as any).cod_item
      );
      if (itemByCode) {
        return itemByCode;
      }
    }

    return null;
  };

  const handleBarClick = (bar: any) => {
    if (!onItemClick) return;

    const chartItem =
      bar?.data ??
      chartData.find(item => item[nameKey] === bar?.indexValue);

    const originalItem = resolveOriginalItem(chartItem);

    if (originalItem) {
      onItemClick(originalItem);
    }
  };

  // Função para lidar com clique nos labels do eixo Y (nomes dos produtos)
  const handleLabelClick = (labelValue: string) => {
    if (!onItemClick) return;

    const clickedItem = chartData.find(item =>
      item[nameKey] === labelValue
    );

    const originalItem = resolveOriginalItem(clickedItem);

    if (originalItem) {
      onItemClick(originalItem);
    }
  };

  if (loading) {
    return (
      <Card sx={{ 
        p: 2, 
        mt: 0, 
        maxHeight: height,
        overflow: 'visible'
      }}>
        <CardContent sx={{ p: 0, overflow: 'visible' }}>
          <Typography
            variant="subtitle1"
            align="center"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            {title}
          </Typography>
          <HorizontalBarChartSkeleton height={height - 100} showTitle={false} />
        </CardContent>
      </Card>
    );
  }

  if (!safeData || safeData.length === 0) {
    return (
      <Card sx={{ 
        p: 2, 
        mt: 0, 
        maxHeight: height,
        overflow: 'visible'
      }}>
        <CardContent sx={{ p: 0, overflow: 'visible' }}>
          <Typography
            variant="subtitle1"
            align="center"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            {title}
          </Typography>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: height - 100,
              color: 'text.secondary'
            }}
          >
            <Typography variant="body1">
              📊 Nenhum dado disponível
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const chartConfig = getHorizontalBarChartConfig(chartData.length, hasLongLabels);

  const fixedContentHeight = 470;
  const tableHeight = tableData.length > 0 ? 150 : 0;
  const chartHeight = fixedContentHeight - tableHeight; 
  const totalCardHeight = fixedContentHeight + 100;

  return (
    <Card sx={{ 
      borderRadius: 'var(--border-radius-md)', 
      boxShadow: 'var(--shadow-card)', 
      p: 2, 
      mt: 0, 
      height: height || totalCardHeight,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <CardContent sx={{ 
        p: 0, 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        '&:last-child': { pb: 0 }
      }}>
        <Typography
          variant="subtitle1"
          align="center"
          sx={{ color: theme.palette.text.secondary, mb: 2, flexShrink: 0 }}
        >
          {title}
        </Typography>

        <Box 
          sx={{ 
            height: chartHeight,
            flexShrink: 0,
            overflow: 'visible',
            position: 'relative',
            zIndex: 1,
            mb: 1,
            // Adicionar cursor pointer nos labels do eixo Y quando há callback de clique
            ...(onItemClick && {
              '& .nivo-axis-left text': {
                cursor: 'pointer',
                '&:hover': {
                  fill: theme.palette.primary.main,
                  fontWeight: 600
                }
              }
            })
          }}
          onClick={(e) => {
            // Capturar cliques nos labels do eixo Y
            if (onItemClick) {
              const target = e.target as HTMLElement;
              if (target.tagName === 'text' && target.closest('.nivo-axis-left')) {
                const labelValue = target.textContent;
                if (labelValue) {
                  handleLabelClick(labelValue);
                }
              }
            }
          }}
        >
          <ResponsiveBar
            data={chartData}
            keys={['valorDisplay']}
            indexBy={nameKey}
            layout="horizontal"
            margin={{
              ...chartConfig.margin,
            }}
            padding={chartConfig.padding}
            innerPadding={chartConfig.innerPadding}
            colors={(bar) => {
              // Usar a cor dinâmica baseada no sinal do valor original
              const itemData = chartData.find(d => d[nameKey] === bar.indexValue);
              return (itemData as any)?.corBarra || color;
            }}
            colorBy="indexValue"
            borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            axisTop={null}
            axisRight={null}
            axisLeft={{
              ...chartConfig.axisLeft,
              format: (value) => String(value)
            }}
            enableGridX={true}
            enableGridY={true}
            labelSkipWidth={30}

            // Muda a cor do texto do rótulo para garantir a legibilidade em barras muito pequenas
            labelTextColor={(datum) => {
              // Usar valorDisplay para comparação
              const currentValue = (datum.data as any).valorDisplay || Math.abs((datum.data as { [key: string]: any })[valueKey] as number);
              const threshold = maxValue * 0.07;
              return currentValue < threshold
                ? theme.palette.text.primary
                : '#ffffff';
            }}
            
            labelFormat={(value) => formatValue(Number(value))}
            label={(datum) => formatValue(Number(datum.value))}
            valueFormat={formatValue}
            theme={{
              labels: {
                text: {
                  fontSize: 11,
                  fontWeight: 600,
                  textAlign: 'right'
                }
              },
              axis: {
                ticks: {
                  text: {
                    fontSize: 11
                  }
                }
              }
            }}
            tooltip={({ id, value, data: itemData }) => {
              const valorOriginal = (itemData as any).valorOriginal;
              const isPositivo = valorOriginal >= 0;
              
              // Determinar cor do valor no tooltip
              let valorColor = 'primary.main';
              let descricao = 'Valor total das operações';
              
              if (useDynamicColors && title.includes('ICMS')) {
                valorColor = isPositivo ? '#f44336' : '#4caf50';
                descricao = isPositivo ? '⚠️ Saldo a pagar (débitos > créditos)' : '✅ Crédito disponível (créditos > débitos)';
              } else if (title.includes('Créditos')) {
                valorColor = '#4caf50';
                descricao = '✅ Crédito de ICMS';
              } else if (title.includes('Débitos')) {
                valorColor = '#f44336';
                descricao = '⚠️ Débito de ICMS';
              } else if (title.includes('Desconto')) {
                descricao = 'Total de descontos';
              } else if (title.includes('Bonificação')) {
                descricao = 'Total de bonificações';
              }
              
              return (
                <Box
                  sx={{
                    background: theme.palette.background.paper,
                    p: 2,
                    minWidth: 280,
                    maxWidth: 400,
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    📊 {(itemData as any).nomeCompleto || id}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 1,
                      color: valorColor
                    }}
                  >
                    {formatValue(valorOriginal)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {descricao}
                  </Typography>
                  {onItemClick && (
                    <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 1, fontWeight: 500 }}>
                      💡 Clique para análise detalhada
                    </Typography>
                  )}
                </Box>
              );
            }}
            onClick={onItemClick ? handleBarClick : undefined}
            animate={true}
            motionConfig="gentle"
          />
        </Box>

        {tableData.length > 0 && (
          <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mb: 1,
              mt: 1,
              flexShrink: 0
            }}>
              <Typography
                variant="subtitle2"
                sx={{ color: theme.palette.text.secondary, mr: 1 }}
              >
                {tableTitle}
              </Typography>
              <Chip 
                label={`${tableData.length} registros`}
                size="small"
                color="info"
                variant="outlined"
              />
            </Box>
            
            <TableContainer
              component={Paper}
              sx={{
                height: tableHeight,
                flexShrink: 0,
                border: `1px solid ${theme.palette.divider}`,
                overflow: 'auto',
                '& .MuiTableHead-root': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  '& .MuiTableCell-root': {
                    backgroundColor: theme.palette.grey[50],
                    borderBottom: `2px solid ${theme.palette.divider}`
                  }
                },
                '& .MuiTableBody-root': {
                  '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                    paddingBottom: '12px'
                  }
                }
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((item, index) => (
                    <TableRow 
                      key={index}
                      hover={!!onItemClick}
                      sx={{ 
                        cursor: onItemClick ? 'pointer' : 'default',
                        '&:hover': onItemClick ? { backgroundColor: 'action.hover' } : {},
                        '&:nth-of-type(odd)': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                      onClick={() => onItemClick && onItemClick(item)}
                    >
                      <TableCell 
                        sx={{ 
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={String(item[nameKey])}
                      >
                        {item[nameKey]}
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          fontWeight={500}
                          sx={{
                            color: useDynamicColors 
                              ? ((item[valueKey] as number) >= 0 ? '#f44336' : '#4caf50')
                              : 'primary.main'
                          }}
                        >
                          {formatValue(item[valueKey] as number)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TopChartWithTable;